import { api } from './api';

/**
 * Client for the Shape Up entity-comment API (/api/shape-up/comments).
 *
 * These are the user-authored, threaded comments on Scope / Bet / Pitch — not
 * the legacy estimation comments in commentsApi.js. The server serializes every
 * comment identically for REST and socket, so the shapes returned here match the
 * payloads useEntityComments receives over the wire.
 */

/**
 * List top-level comments (with nested replies) for an entity.
 * @param {'scope'|'bet'|'pitch'} type
 * @param {string} id - entity UUID
 * @returns {Promise<Array>} top-level comments, oldest first
 */
export const getEntityComments = async (type, id) => {
  const res = await api.get(`/shape-up/comments/${type}/${id}`);
  return res.data.comments;
};

/**
 * Create a comment or reply. Author is the logged-in user (from the token).
 * @param {Object} params
 * @param {'scope'|'bet'|'pitch'} params.commentableType
 * @param {string} params.commentableId
 * @param {string} params.content
 * @param {string} params.organizationId
 * @param {string|null} [params.parentCommentId] - set for a reply
 * @returns {Promise<Object>} the created comment
 */
export const createEntityComment = async ({
  commentableType,
  commentableId,
  content,
  organizationId,
  parentCommentId = null,
}) => {
  const res = await api.post('/shape-up/comments', {
    commentableType,
    commentableId,
    content,
    organizationId,
    parentCommentId,
  });
  return res.data;
};

/**
 * Edit a comment. Author only; Jira-sourced comments are rejected server-side.
 * @param {string} commentId
 * @param {string} content
 * @returns {Promise<Object>} the updated comment
 */
export const updateEntityComment = async (commentId, content) => {
  const res = await api.put(`/shape-up/comments/${commentId}`, { content });
  return res.data;
};

/**
 * Delete a comment. Author or an org admin/owner.
 * @param {string} commentId
 * @returns {Promise<Object>} { message, commentId }
 */
export const deleteEntityComment = async (commentId) => {
  const res = await api.delete(`/shape-up/comments/${commentId}`);
  return res.data;
};

/**
 * Mark the thread on an entity as read for the current user, clearing the unread
 * dot. Fire-and-forget: the server advances the read watermark and returns 204.
 * @param {'scope'|'bet'|'pitch'} type
 * @param {string} id - entity UUID
 * @returns {Promise<void>}
 */
export const markCommentsRead = async (type, id) => {
  await api.post(`/shape-up/comments/${type}/${id}/read`);
};

const entityCommentsApi = {
  getEntityComments,
  createEntityComment,
  updateEntityComment,
  deleteEntityComment,
  markCommentsRead,
};

export default entityCommentsApi;
